import {
  CallSplit,
  CropSquare,
  DeviceHub,
  FilterNone,
  MapsHomeWork,
  SvgIconComponent,
  Timeline,
  VerticalAlignCenter,
  ViewDay,
} from "@mui/icons-material";
import type { Geometry } from "geojson";

import { inAddress, inParcel, inPole, inSpan, inStreet, type LayerGroupIds } from "fond/layers";
import { LayerId, LayerIds, WORKFLOW_KEYS, WorkflowKey } from "fond/types";
import type { BaseDataLayerName } from "fond/types/layer";

export type BaseDataLayerKey = "boundary" | "addresses" | "parcels" | "streets" | "ug" | "aerial" | "leadins" | "sources";

/** Base data layers that FOND can generate. */
export type GenerationKey = Extract<BaseDataLayerKey, "ug" | "leadins">;

/** A way of populating a base data layer, offered as an option in its flyout. */
export type BaseDataOption = "generate" | "importLayer" | "importWithinBoundary" | "upload" | "draw";

export type BaseDataGroupId = "designArea" | "inputs" | "candidateNetwork" | "networkPoints";

/** A planner layer of a base data layer. */
export type BaseDataPlannerLayerDefinition = {
  id: LayerId;
  /** Lowercase noun telling it apart from the base data layer's other planner layers, e.g. "points"; the base data layer's noun when it is the only one. */
  name: string;
  /** Title of its upload section, e.g. "Underground points". */
  title: string;
  /** Singular and plural nouns for its features. */
  units: [string, string];
  geometryType: Geometry["type"];
  /** FOND layer downloaded to import FOND data into it, when FOND has data for it. */
  importSource?: BaseDataLayerName;
};

export type BaseDataLayerDefinition = {
  key: BaseDataLayerKey;
  name: string;
  /** Short lowercase name, when `name` is too long for inline copy. */
  shortName?: string;
  /** Lowercase noun for the layer's contents in copy, e.g. "aerial spans & poles". */
  noun: string;
  icon: SvgIconComponent;
  info: string;
  group: BaseDataGroupId;
  /** Whether design needs this layer. */
  required?: boolean;
  options: BaseDataOption[];
  /** Planner layers that together make up this base data layer; empty for the boundary (`version.UploadedArea`). */
  layers: BaseDataPlannerLayerDefinition[];
  /** Input layer group of the layer, which polygon select reads to decide which layers to import; null for the boundary. */
  layerGroupId: keyof typeof LayerGroupIds | null;
};

export type BaseDataGroup = {
  id: BaseDataGroupId;
  label: string;
  keys: BaseDataLayerKey[];
};

/** Whether each base data layer has data. */
export type BaseDataPresence = Record<BaseDataLayerKey, boolean>;

/** Planner layers of each base data layer that have no data. */
export type BaseDataMissingLayers = Record<BaseDataLayerKey, LayerId[]>;

export const BASE_DATA_LAYERS: Record<BaseDataLayerKey, BaseDataLayerDefinition> = {
  boundary: {
    key: "boundary",
    name: "Boundary",
    noun: "boundary",
    icon: CropSquare,
    info: "The extent of your design area.",
    group: "designArea",
    options: ["importWithinBoundary", "upload", "draw"],
    layers: [],
    layerGroupId: null,
  },
  addresses: {
    key: "addresses",
    name: "Addresses",
    noun: "addresses",
    icon: MapsHomeWork,
    info: "Locations that require fiber service. Fiber allocation to each address can be controlled via the Demand model and the address type.",
    group: "inputs",
    required: true,
    options: ["importLayer", "upload", "draw"],
    layers: [
      {
        id: LayerIds.inAddress,
        name: "addresses",
        title: "Addresses",
        units: ["address", "addresses"],
        geometryType: "Point",
        importSource: inAddress,
      },
    ],
    layerGroupId: "inAddress",
  },
  parcels: {
    key: "parcels",
    name: "Parcels",
    noun: "parcels",
    icon: FilterNone,
    info: "Polygons describing land lots. Parcels are used to generate other layers, such as underground path, leadins, and service area boundaries.",
    group: "inputs",
    required: true,
    options: ["importLayer", "upload", "draw"],
    layers: [
      { id: LayerIds.inParcel, name: "parcels", title: "Parcels", units: ["parcel", "parcels"], geometryType: "Polygon", importSource: inParcel },
    ],
    layerGroupId: "inParcel",
  },
  streets: {
    key: "streets",
    name: "Streets",
    noun: "streets",
    icon: ViewDay,
    info: "Road centerlines. Streets are used to generate underground path and aerial spans.",
    group: "inputs",
    required: true,
    options: ["importLayer", "upload", "draw"],
    layers: [
      // FOND streets are downloaded from its underground path layer.
      {
        id: LayerIds.inBaseDataStreet,
        name: "streets",
        title: "Streets",
        units: ["street", "streets"],
        geometryType: "LineString",
        importSource: inStreet,
      },
    ],
    layerGroupId: "inBaseDataStreet",
  },
  ug: {
    key: "ug",
    name: "Underground path & points",
    shortName: "underground",
    noun: "underground path & points",
    icon: Timeline,
    info: "All possible locations where underground fiber cables and devices may be placed in the Design step.",
    group: "candidateNetwork",
    options: ["generate", "upload", "draw"],
    layers: [
      { id: LayerIds.inStreet, name: "path", title: "Underground path", units: ["path", "paths"], geometryType: "LineString" },
      { id: LayerIds.inStreetPoints, name: "points", title: "Underground points", units: ["point", "points"], geometryType: "Point" },
    ],
    layerGroupId: "inUnderground",
  },
  aerial: {
    key: "aerial",
    name: "Aerial spans & poles",
    shortName: "aerial",
    noun: "aerial spans & poles",
    icon: VerticalAlignCenter,
    info: "All possible locations where aerial fiber cables and devices may be placed in the Design step.",
    group: "candidateNetwork",
    options: ["importLayer", "upload", "draw"],
    layers: [
      { id: LayerIds.inSpan, name: "spans", title: "Aerial spans", units: ["span", "spans"], geometryType: "LineString", importSource: inSpan },
      { id: LayerIds.inPole, name: "poles", title: "Poles", units: ["pole", "poles"], geometryType: "Point", importSource: inPole },
    ],
    layerGroupId: "spanPole",
  },
  leadins: {
    key: "leadins",
    name: "Leadins",
    noun: "leadins",
    icon: CallSplit,
    info: "The approximate service path from the main network to an address location.",
    group: "candidateNetwork",
    required: true,
    options: ["generate", "upload", "draw"],
    layers: [{ id: LayerIds.inLeadIns, name: "leadins", title: "Leadins", units: ["leadin", "leadins"], geometryType: "LineString" }],
    layerGroupId: "inLeadIns",
  },
  sources: {
    key: "sources",
    name: "Sources",
    noun: "sources",
    icon: DeviceHub,
    info: "Points that feed the network. This may be a central office, distribution cabinet, or a tie-in point to an existing network. Leave empty to allow FOND to select its own locations.",
    group: "networkPoints",
    options: ["upload", "draw"],
    layers: [{ id: LayerIds.inExchange, name: "sources", title: "Sources", units: ["source", "sources"], geometryType: "Point" }],
    layerGroupId: "inExchange",
  },
};

export const BASE_DATA_GROUPS: BaseDataGroup[] = [
  { id: "designArea", label: "DESIGN AREA", keys: ["boundary"] },
  { id: "inputs", label: "INPUTS", keys: ["addresses", "parcels", "streets"] },
  { id: "candidateNetwork", label: "CANDIDATE NETWORK", keys: ["ug", "aerial", "leadins"] },
  { id: "networkPoints", label: "NETWORK POINTS", keys: ["sources"] },
];

export const BASE_DATA_LAYER_KEYS: BaseDataLayerKey[] = BASE_DATA_GROUPS.flatMap((group) => group.keys);

/** IDs of the planner layers of each base data layer. */
export const BASE_DATA_LAYER_IDS = Object.fromEntries(
  BASE_DATA_LAYER_KEYS.map((key) => [key, BASE_DATA_LAYERS[key].layers.map(({ id }) => id)])
) as Record<BaseDataLayerKey, LayerId[]>;

/** Base data layers the Modify data tools work on. */
export const MODIFY_DATA_KEYS = ["addresses", "parcels", "streets", "ug", "aerial", "leadins"] as const satisfies readonly BaseDataLayerKey[];

/** Inputs required before any base data can be generated. */
export const REQUIRED_INPUT_KEYS = ["addresses", "parcels", "streets"] as const satisfies readonly BaseDataLayerKey[];

/** Generation workflows in the order they run. */
export const GENERATION_KEYS = ["ug", "leadins"] as const satisfies readonly GenerationKey[];

export const isGenerationKey = (key: BaseDataLayerKey): key is GenerationKey => (GENERATION_KEYS as readonly BaseDataLayerKey[]).includes(key);

export const GENERATION_WORKFLOW_KEYS: Record<GenerationKey, WorkflowKey> = {
  ug: WORKFLOW_KEYS.PLANNER_EXTENDED.UG_GENERATION,
  leadins: WORKFLOW_KEYS.PLANNER_EXTENDED.LEADIN_GENERATION,
};

/** Number of features in each planner layer, if known. */
export type BaseDataLayerCounts = Partial<Record<LayerId, number | null | undefined>>;

/** A planner layer of a base data layer and its data. */
export type BaseDataPlannerLayer = {
  id: LayerId;
  /** Lowercase noun for the planner layer in copy, e.g. "underground points". */
  name: string;
  /** Singular and plural nouns for its features. */
  units: [string, string];
  hasData: boolean;
  /** Number of features, if known. */
  count: number | null;
};

/** Return the planner layers of a base data layer with their data, given which have none and their feature counts. */
export const getBaseDataPlannerLayers = (
  key: BaseDataLayerKey,
  missing: readonly LayerId[],
  counts: BaseDataLayerCounts = {}
): BaseDataPlannerLayer[] => {
  const { layers, shortName, noun } = BASE_DATA_LAYERS[key];
  return layers.map(({ id, name, units }) => ({
    id,
    name: layers.length > 1 ? `${shortName ?? noun} ${name}` : noun,
    units,
    hasData: !missing.includes(id),
    count: counts[id] ?? null,
  }));
};

/** Return a feature count with its noun, e.g. "1,234 paths" or "1 point". */
export const formatCount = (count: number, [singular, plural]: readonly [string, string]): string =>
  `${count.toLocaleString()} ${count === 1 ? singular : plural}`;

/** Return which base data layers have data, given the version's planner layer IDs and whether it has an input data area. */
export const getBaseDataPresence = (layerKeys: readonly string[], hasBoundary: boolean): BaseDataPresence =>
  Object.fromEntries(
    BASE_DATA_LAYER_KEYS.map((key) => [
      key,
      key === "boundary" ? hasBoundary : BASE_DATA_LAYER_IDS[key].every((layerId) => layerKeys.includes(layerId)),
    ])
  ) as BaseDataPresence;

/** Return the planner layers of each base data layer that have no data, given the version's planner layer IDs. */
export const getBaseDataMissingLayers = (layerKeys: readonly string[]): BaseDataMissingLayers =>
  Object.fromEntries(
    BASE_DATA_LAYER_KEYS.map((key) => [key, BASE_DATA_LAYER_IDS[key].filter((layerId) => !layerKeys.includes(layerId))])
  ) as BaseDataMissingLayers;

/** Return which base data layers have data in any of their planner layers. */
export const getBaseDataAnyPresence = (missing: BaseDataMissingLayers, hasBoundary: boolean): BaseDataPresence =>
  Object.fromEntries(
    BASE_DATA_LAYER_KEYS.map((key) => [key, key === "boundary" ? hasBoundary : missing[key].length < BASE_DATA_LAYER_IDS[key].length])
  ) as BaseDataPresence;
